import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { DateInput } from "@/components/ui/DateInput";
import { Input } from "@/components/ui/Input";
import { Loader } from "@/components/ui/Loader";
import { Modal } from "@/components/ui/Modal";
import { Select } from "@/components/ui/Select";
import type {
  ChaoPeriodoCalculado,
  ChaoPeriodoSugerido,
  ChaoProdutividadeAnoMes,
  ChaoProdutividadeMes,
  ChaoProdutividadeStatus,
  ChaoRegraBaseAvanco,
} from "@/services/chaoDeObra";
import {
  createChaoPeriodoAvanco,
  deleteChaoPeriodoAvanco,
  fetchChaoProdutividade,
  fetchChaoProdutividadeAnual,
  saveChaoPeriodosLote,
  saveChaoRegraBaseAvanco,
  sugerirChaoPeriodos,
  updateChaoPeriodoAvanco,
} from "@/services/chaoDeObra";
import { formatDate, formatNumber } from "@/utils/format";
import type { ComercialUnitRow } from "@/services/comercial";
import { resolveConstructiveUnitLabel } from "./chaoUnitCardUtils";

interface Props {
  costCenterId: string;
  units: ComercialUnitRow[];
  unitLinksByCc: Record<string, Record<string, string>>;
  constructiveUnitOptions: { value: string; label: string }[];
  canEdit: boolean;
  initialConstructiveUnit?: string;
}

const STATUS_LABEL: Record<ChaoProdutividadeStatus, string> = {
  atingiu: "Atingiu",
  atencao: "Atenção",
  abaixo: "Abaixo",
  sem_meta: "Sem meta",
};

const STATUS_CLASS: Record<ChaoProdutividadeStatus, string> = {
  atingiu: "bg-emerald-50 text-emerald-700 border-emerald-200",
  atencao: "bg-amber-50 text-amber-800 border-amber-200",
  abaixo: "bg-red-50 text-red-700 border-red-200",
  sem_meta: "bg-slate-100 text-slate-600 border-slate-200",
};

function currentCompetencia(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function pp(value: number): string {
  return `${formatNumber(value, 2)} pp`;
}

function pctLabel(value: number | null): string {
  if (value == null) return "—";
  return `${formatNumber(value, 2)}%`;
}

function pontoLabel(at: string | null, value: number): string {
  const data = at ? formatDate(at) : "—";
  return `${data} · ${formatNumber(value, 2)}%`;
}

function competenciaLabel(ym: string): string {
  const [y, m] = ym.split("-");
  const d = new Date(Number(y), Number(m) - 1, 1);
  if (Number.isNaN(d.getTime())) return ym;
  return d.toLocaleDateString("pt-BR", { month: "short", year: "numeric" });
}

function emptyTotais(): ChaoProdutividadeMes["totais"] {
  return {
    avancoRealMesPp: 0,
    metaMesPp: 0,
    atingimentoMesPct: null,
    semanasAtingidas: 0,
    totalSemanas: 0,
  };
}

interface Draft {
  id?: string;
  numero: string;
  descricao: string;
  dataInicio: string;
  dataFim: string;
  meta: string;
  metaPorUc: Record<string, string>;
  observacao: string;
}

function parseMetaNumber(raw: string): number {
  const n = Number(String(raw).replace(",", "."));
  return Number.isFinite(n) ? n : 0;
}

export function ChaoProdutividadeView({
  costCenterId,
  units,
  unitLinksByCc,
  constructiveUnitOptions,
  canEdit,
  initialConstructiveUnit,
}: Props) {
  const [competencia, setCompetencia] = useState(currentCompetencia);
  const [selectedUc, setSelectedUc] = useState(initialConstructiveUnit ?? "");
  const [mes, setMes] = useState<ChaoProdutividadeMes | null>(null);
  const [anual, setAnual] = useState<ChaoProdutividadeAnoMes[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [excluir, setExcluir] = useState<ChaoPeriodoCalculado | null>(null);
  const [sugestao, setSugestao] = useState<ChaoPeriodoSugerido[] | null>(null);
  const [sugestaoLoading, setSugestaoLoading] = useState(false);
  const [recorte, setRecorte] = useState<"mes" | "quinzena" | "semana" | "dia">("semana");
  const sugestaoPedido = useRef(0);

  const ucOptions = useMemo(() => {
    const fromBudget = constructiveUnitOptions.map((o) => o.label.trim()).filter(Boolean);
    const fromLinks = Object.values(unitLinksByCc[costCenterId] ?? {}).map((x) => x.trim()).filter(Boolean);
    return [...new Set([...fromBudget, ...fromLinks])].sort((a, b) => a.localeCompare(b, "pt-BR"));
  }, [constructiveUnitOptions, unitLinksByCc, costCenterId]);

  const scopedUnits = useMemo(() => {
    if (!selectedUc) return units;
    return units.filter((u) => resolveConstructiveUnitLabel(u, costCenterId, unitLinksByCc) === selectedUc);
  }, [units, selectedUc, costCenterId, unitLinksByCc]);

  const unitIds = useMemo(() => scopedUnits.map((u) => u.id), [scopedUnits]);
  const idsKey = unitIds.join(",");

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setMes(null);
    const ids = idsKey ? idsKey.split(",") : [];
    const [row, ano] = await Promise.all([
      fetchChaoProdutividade({ costCenterId, competencia, unitIds: ids, constructiveUnit: selectedUc || undefined }),
      fetchChaoProdutividadeAnual({ costCenterId, unitIds: ids, constructiveUnit: selectedUc || undefined }),
    ]);
    if (!row.success) {
      setMes(null);
      setError(row.error ?? "Falha ao carregar a produtividade.");
    } else {
      setMes(row);
    }
    if (ano.success) setAnual(ano.meses);
    else setAnual([]);
    setLoading(false);
  }, [costCenterId, competencia, idsKey, selectedUc]);

  useEffect(() => {
    void load();
  }, [load]);

  const periodos = mes?.periodos ?? [];
  const totais = mes?.totais ?? emptyTotais();
  const regra = mes?.regraBase ?? "ULTIMA_ANTERIOR";

  const barras = useMemo(
    () =>
      periodos.map((p) => ({
        nome: p.descricao?.trim() || `S${p.numero}`,
        real: p.avancoRealPp,
        meta: p.metaAvancoPp,
        atingimento: p.atingimentoPct ?? 0,
      })),
    [periodos],
  );

  const acumulado = useMemo(
    () =>
      (mes?.serieAcumulada ?? []).map((p) => ({
        nome: p.descricao?.trim() || `S${p.numero}`,
        real: p.realAcumuladoPp,
        meta: p.metaAcumuladaPp,
      })),
    [mes],
  );

  const anualChart = useMemo(
    () =>
      anual.map((m) => ({
        nome: competenciaLabel(m.competencia),
        real: m.avancoRealMesPp,
        meta: m.metaMesPp,
      })),
    [anual],
  );

  const salvarRegra = async (value: string) => {
    if (!canEdit) return;
    const regraBase = value as ChaoRegraBaseAvanco;
    const res = await saveChaoRegraBaseAvanco(costCenterId, regraBase);
    if (!res.success) {
      setError(res.error ?? "Falha ao salvar a regra de base.");
      return;
    }
    await load();
  };

  const carregarSugestao = async (next: "mes" | "quinzena" | "semana" | "dia") => {
    const pedido = ++sugestaoPedido.current;
    setRecorte(next);
    setSugestaoLoading(true);
    setFormError(null);
    const res = await sugerirChaoPeriodos(competencia, next);
    if (pedido !== sugestaoPedido.current) return;
    setSugestaoLoading(false);
    if (!res.success) {
      setFormError(res.error ?? "Falha ao montar os períodos.");
      setSugestao([]);
      return;
    }
    const byNumero = new Map(periodos.map((p) => [p.numero, p]));
    setSugestao(
      res.periodos.map((row) => {
        const prev = byNumero.get(row.numero);
        return {
          ...row,
          metaAvancoPp: prev?.metaObraPp ?? prev?.metaAvancoPp ?? row.metaAvancoPp,
          metaPorUc: { ...(prev?.metaPorUc ?? {}) },
        };
      }),
    );
  };

  const abrirMontar = () => {
    if (!canEdit) return;
    setFormError(null);
    if (periodos.length > 0) {
      setRecorte("semana");
      setSugestao(
        periodos.map((p) => ({
          numero: p.numero,
          descricao: p.descricao ?? `Período ${p.numero}`,
          dataInicio: p.dataInicio,
          dataFim: p.dataFim,
          metaAvancoPp: p.metaObraPp ?? p.metaAvancoPp,
          metaPorUc: { ...(p.metaPorUc ?? {}) },
        })),
      );
      return;
    }
    setSugestao([]);
    void carregarSugestao("semana");
  };

  const fecharMontar = () => {
    sugestaoPedido.current += 1;
    setSugestaoLoading(false);
    setSugestao(null);
  };

  const salvarSugestao = async () => {
    if (!sugestao?.length) return;
    setSaving(true);
    setFormError(null);
    const res = await saveChaoPeriodosLote({
      costCenterId,
      competencia,
      periodos: sugestao,
      unitIds,
      substituir: true,
      constructiveUnit: selectedUc || undefined,
    });
    setSaving(false);
    if (!res.success) {
      setFormError(res.error ?? "Não foi possível salvar os períodos.");
      return;
    }
    setSugestao(null);
    await load();
  };

  const atualizarLinha = (index: number, patch: Partial<ChaoPeriodoSugerido>) => {
    setSugestao((rows) => rows?.map((row, idx) => (idx === index ? { ...row, ...patch } : row)) ?? null);
  };

  const abrirEdicao = (p: ChaoPeriodoCalculado) => {
    if (!canEdit) return;
    setFormError(null);
    const mapa: Record<string, string> = {};
    for (const label of ucOptions) mapa[label] = String(p.metaPorUc?.[label] ?? "");
    setDraft({
      id: p.id,
      numero: String(p.numero),
      descricao: p.descricao ?? "",
      dataInicio: p.dataInicio,
      dataFim: p.dataFim,
      meta: String(p.metaObraPp ?? p.metaAvancoPp),
      metaPorUc: mapa,
      observacao: p.observacao ?? "",
    });
  };

  const salvarDraft = async () => {
    if (!draft) return;
    setSaving(true);
    setFormError(null);
    const metaPorUc: Record<string, number> = {};
    for (const [label, value] of Object.entries(draft.metaPorUc)) {
      if (!label.trim()) continue;
      metaPorUc[label] = parseMetaNumber(value);
    }
    const payload = {
      costCenterId,
      competencia,
      numero: Number(draft.numero),
      descricao: draft.descricao,
      dataInicio: draft.dataInicio,
      dataFim: draft.dataFim,
      metaAvancoPp: parseMetaNumber(draft.meta),
      metaPorUc,
      observacao: draft.observacao,
    };
    const res = draft.id
      ? await updateChaoPeriodoAvanco(draft.id, payload)
      : await createChaoPeriodoAvanco(payload);
    setSaving(false);
    if (!res.success) {
      setFormError(res.error ?? "Não foi possível salvar o período.");
      return;
    }
    setDraft(null);
    await load();
  };

  const confirmarExclusao = async () => {
    if (!excluir) return;
    setSaving(true);
    const res = await deleteChaoPeriodoAvanco(excluir.id);
    setSaving(false);
    if (!res.success) {
      setError(res.error ?? "Falha ao excluir o período.");
      return;
    }
    setExcluir(null);
    await load();
  };

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col lg:flex-row lg:items-end gap-4">
        <Input
          label="Competência"
          type="month"
          value={competencia}
          onChange={(e) => setCompetencia(e.target.value)}
          className="max-w-[220px]"
        />
        <Select
          label="Recorte da meta"
          value={selectedUc}
          placeholder="Obra inteira"
          options={ucOptions.map((label) => ({ value: label, label }))}
          onChange={(e) => setSelectedUc(e.target.value)}
          className="max-w-[260px]"
        />
        <div className="flex-1 max-w-xl">
          <Select
            label="Regra da base"
            value={regra}
            options={[
              { value: "ULTIMA_ANTERIOR", label: "Última medição anterior" },
              { value: "PRIMEIRA_DO_PERIODO", label: "Primeira medição do período" },
            ]}
            onChange={(e) => void salvarRegra(e.target.value)}
            disabled={!canEdit}
          />
          <p className="text-xs text-slate-500 mt-1.5">
            {selectedUc
              ? `A meta e o avanço real usam só as casas vinculadas a ${selectedUc}.`
              : regra === "PRIMEIRA_DO_PERIODO"
                ? "A base é o avanço da obra no instante do primeiro relatório aprovado dentro do período. Sem relatório no período, a base é igual ao final."
                : "A base é o avanço da obra no último relatório aprovado antes do início do período. Sem relatório anterior, a base é 0."}
          </p>
        </div>
      </div>
      {selectedUc && scopedUnits.length === 0 && (
        <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          Nenhuma casa vinculada a {selectedUc}. Use Vínculos UC para associar as unidades.
        </p>
      )}

      <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
        <Card label="Avanço real no mês" value={pp(totais.avancoRealMesPp)} />
        <Card label="Meta do mês" value={pp(totais.metaMesPp)} />
        <Card label="% Atingimento" value={pctLabel(totais.atingimentoMesPct)} />
        <Card
          label="Períodos que bateram a meta"
          value={`${totais.semanasAtingidas} de ${totais.totalSemanas}`}
        />
      </div>

      {error && (
        <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</p>
      )}

      {canEdit && (
        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" leftIcon={<Plus className="w-4 h-4" />} onClick={abrirMontar}>
            Definir períodos e metas
          </Button>
        </div>
      )}

      {(mes?.warnings ?? []).length > 0 && (
        <ul className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 space-y-1">
          {mes!.warnings.map((w) => (
            <li key={w}>{w}</li>
          ))}
        </ul>
      )}

      {loading && !mes ? (
        <div className="flex items-center gap-2 text-sm text-slate-500 py-8 justify-center">
          <Loader size="sm" />
          Carregando produtividade…
        </div>
      ) : (
        <div className="border border-slate-200 rounded-xl overflow-x-auto bg-white">
          <table className="w-full text-sm min-w-[980px]">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
              <tr>
                <th className="text-left px-3 py-2">Nº</th>
                <th className="text-left px-3 py-2">Início</th>
                <th className="text-left px-3 py-2">Fim</th>
                <th className="text-left px-3 py-2">Base</th>
                <th className="text-left px-3 py-2">Final</th>
                <th className="text-right px-3 py-2">Avanço real</th>
                <th className="text-right px-3 py-2">Meta</th>
                <th className="text-right px-3 py-2">Desvio</th>
                <th className="text-right px-3 py-2">% Atingimento</th>
                <th className="text-left px-3 py-2">Status</th>
                {canEdit && <th className="text-right px-3 py-2">Ações</th>}
              </tr>
            </thead>
            <tbody>
              {periodos.length === 0 && (
                <tr>
                  <td colSpan={canEdit ? 11 : 10} className="px-3 py-8 text-center text-slate-500">
                    Nenhum período cadastrado para este mês. {canEdit ? "Use Definir períodos e metas." : ""}
                  </td>
                </tr>
              )}
              {periodos.map((p) => (
                <tr key={p.id} className="border-t border-slate-100">
                  <td className="px-3 py-2 font-semibold">
                    {p.numero}
                    {p.descricao ? <div className="text-xs font-normal text-slate-500">{p.descricao}</div> : null}
                  </td>
                  <td className="px-3 py-2">{formatDate(p.dataInicio)}</td>
                  <td className="px-3 py-2">{formatDate(p.dataFim)}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{pontoLabel(p.base.at, p.base.pct)}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{pontoLabel(p.final.at, p.final.pct)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{pp(p.avancoRealPp)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{pp(p.metaAvancoPp)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{pp(p.desvioPp)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{pctLabel(p.atingimentoPct)}</td>
                  <td className="px-3 py-2">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-semibold border ${STATUS_CLASS[p.status]}`}>
                      {STATUS_LABEL[p.status]}
                    </span>
                    {p.semMedicao && (
                      <div className="text-[11px] text-slate-500 mt-1">Sem medição no período</div>
                    )}
                  </td>
                  {canEdit && (
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      <Button size="sm" variant="ghost" onClick={() => abrirEdicao(p)} aria-label="Editar período">
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setExcluir(p)} aria-label="Excluir período">
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {periodos.length > 0 && (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          <ChartCard title="Avanço real × meta">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={barras} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                <XAxis dataKey="nome" tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} width={40} />
                <Tooltip formatter={(v: unknown) => pp(Number(v))} />
                <Legend />
                <Bar dataKey="real" name="Avanço real (pp)" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                <Bar dataKey="meta" name="Meta (pp)" fill="#0f766e" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
          <ChartCard title="Acumulado no mês">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={acumulado} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                <XAxis dataKey="nome" tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} width={40} />
                <Tooltip formatter={(v: unknown) => pp(Number(v))} />
                <Legend />
                <Line type="monotone" dataKey="real" name="Real acumulado" stroke="#f59e0b" strokeWidth={2} dot />
                <Line type="monotone" dataKey="meta" name="Meta acumulada" stroke="#0f766e" strokeWidth={2} dot />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>
          <ChartCard title="% de atingimento">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={barras} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                <XAxis dataKey="nome" tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} width={40} />
                <Tooltip formatter={(v: unknown) => pctLabel(Number(v))} />
                <ReferenceLine y={100} stroke="#64748b" strokeDasharray="4 4" label={{ value: "100%", fontSize: 11, fill: "#64748b" }} />
                <Bar dataKey="atingimento" name="% atingimento" fill="#0284c7" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
          {anualChart.length > 0 && (
            <ChartCard title="Visão anual">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={anualChart} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                  <XAxis dataKey="nome" tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} width={40} />
                  <Tooltip formatter={(v: unknown) => pp(Number(v))} />
                  <Legend />
                  <Bar dataKey="real" name="Avanço real (pp)" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="meta" name="Meta (pp)" fill="#0f766e" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          )}
        </div>
      )}

      <Modal
        open={draft != null}
        onClose={() => setDraft(null)}
        title="Editar período"
        size="md"
        closeOnEscape
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setDraft(null)} disabled={saving}>
              Cancelar
            </Button>
            <Button onClick={() => void salvarDraft()} loading={saving} disabled={saving}>
              Salvar
            </Button>
          </div>
        }
      >
        {draft && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Input label="Nº" type="number" min={1} value={draft.numero} onChange={(e) => setDraft({ ...draft, numero: e.target.value })} />
            <Input label="Descrição" value={draft.descricao} onChange={(e) => setDraft({ ...draft, descricao: e.target.value })} />
            <DateInput label="Início" value={draft.dataInicio} onChange={(v) => setDraft({ ...draft, dataInicio: v })} />
            <DateInput label="Fim" value={draft.dataFim} onChange={(v) => setDraft({ ...draft, dataFim: v })} />
            <Input label="Meta da obra (pp)" type="number" min={0} step="0.01" value={draft.meta} onChange={(e) => setDraft({ ...draft, meta: e.target.value })} />
            <Input label="Observação" value={draft.observacao} onChange={(e) => setDraft({ ...draft, observacao: e.target.value })} />
            {ucOptions.length > 0 && (
              <div className="sm:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
                <p className="sm:col-span-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Metas por unidade construtiva
                </p>
                {ucOptions.map((label) => (
                  <Input
                    key={label}
                    label={`${label} (pp)`}
                    type="number"
                    min={0}
                    step="0.01"
                    value={draft.metaPorUc[label] ?? ""}
                    onChange={(e) =>
                      setDraft({ ...draft, metaPorUc: { ...draft.metaPorUc, [label]: e.target.value } })
                    }
                  />
                ))}
              </div>
            )}
            {formError && <p className="sm:col-span-2 text-sm text-red-600">{formError}</p>}
          </div>
        )}
      </Modal>

      <Modal
        open={sugestao != null}
        onClose={fecharMontar}
        title="Definir períodos e metas"
        size="lg"
        closeOnEscape
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={fecharMontar} disabled={saving}>
              Cancelar
            </Button>
            <Button onClick={() => void salvarSugestao()} loading={saving} disabled={saving || sugestaoLoading || !sugestao?.length}>
              Salvar períodos
            </Button>
          </div>
        }
      >
        <div className="flex flex-col gap-3">
          <Select
            label="Recorte"
            value={recorte}
            options={[
              { value: "mes", label: "Mês inteiro" },
              { value: "quinzena", label: "Quinzena" },
              { value: "semana", label: "Semana" },
              { value: "dia", label: "Dia" },
            ]}
            onChange={(e) => void carregarSugestao(e.target.value as "mes" | "quinzena" | "semana" | "dia")}
          />
          <p className="text-sm text-slate-600">
            O mês selecionado é dividido do jeito que você escolher. Ajuste datas, nomes e metas da obra e de cada
            unidade construtiva antes de salvar. Salvar substitui os períodos deste mês. Relatórios e medições não
            são alterados.
          </p>
          <div className="flex justify-end">
            <Button
              size="sm"
              variant="secondary"
              onClick={() =>
                setSugestao((rows) => {
                  const list = rows ?? [];
                  const numero = list.reduce((max, row) => Math.max(max, row.numero), 0) + 1;
                  return [...list, { numero, descricao: `Período ${numero}`, dataInicio: "", dataFim: "", metaAvancoPp: 0 }];
                })
              }
            >
              Adicionar período
            </Button>
          </div>
          {sugestaoLoading ? (
            <div className="flex items-center gap-2 text-sm text-slate-500 py-6 justify-center">
              <Loader size="sm" />
              Montando períodos…
            </div>
          ) : (
            <div className="border border-slate-200 rounded-xl overflow-auto max-h-[50vh]">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 text-xs uppercase sticky top-0">
                  <tr>
                    <th className="text-left px-3 py-2">Nº</th>
                    <th className="text-left px-3 py-2">Descrição</th>
                    <th className="text-left px-3 py-2">Início</th>
                    <th className="text-left px-3 py-2">Fim</th>
                    <th className="text-left px-3 py-2">Meta obra (pp)</th>
                    {ucOptions.map((label) => (
                      <th key={label} className="text-left px-3 py-2 whitespace-nowrap">{label} (pp)</th>
                    ))}
                    <th className="px-3 py-2" />
                  </tr>
                </thead>
                <tbody>
                  {(sugestao ?? []).map((s, i) => (
                    <tr key={`${s.numero}-${i}`} className="border-t border-slate-100">
                      <td className="px-3 py-2">{s.numero}</td>
                      <td className="px-3 py-2">
                        <input
                          value={s.descricao}
                          onChange={(e) => atualizarLinha(i, { descricao: e.target.value })}
                          className="w-full h-8 rounded-md border border-slate-200 px-2 text-sm"
                        />
                      </td>
                      <td className="px-3 py-2 min-w-[150px]">
                        <DateInput value={s.dataInicio} onChange={(v) => atualizarLinha(i, { dataInicio: v })} />
                      </td>
                      <td className="px-3 py-2 min-w-[150px]">
                        <DateInput value={s.dataFim} onChange={(v) => atualizarLinha(i, { dataFim: v })} />
                      </td>
                      <td className="px-3 py-2">
                        <input
                          type="number"
                          min={0}
                          step="0.01"
                          value={s.metaAvancoPp}
                          onChange={(e) => atualizarLinha(i, { metaAvancoPp: Number(e.target.value) })}
                          className="w-24 h-8 rounded-md border border-slate-200 px-2 text-sm text-right"
                        />
                      </td>
                      {ucOptions.map((label) => (
                        <td key={label} className="px-3 py-2">
                          <input
                            type="number"
                            min={0}
                            step="0.01"
                            value={s.metaPorUc?.[label] ?? ""}
                            onChange={(e) =>
                              atualizarLinha(i, {
                                metaPorUc: { ...(s.metaPorUc ?? {}), [label]: Number(e.target.value) },
                              })
                            }
                            className="w-24 h-8 rounded-md border border-slate-200 px-2 text-sm text-right"
                            aria-label={`Meta ${label} do período ${s.numero}`}
                          />
                        </td>
                      ))}
                      <td className="px-3 py-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          aria-label="Remover período da lista"
                          onClick={() =>
                            setSugestao((rows) =>
                              (rows ?? []).filter((_, idx) => idx !== i).map((row, idx) => ({ ...row, numero: idx + 1 })),
                            )
                          }
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {formError && <p className="text-sm text-red-600">{formError}</p>}
        </div>
      </Modal>

      <Modal
        open={excluir != null}
        onClose={() => setExcluir(null)}
        title="Excluir período"
        size="sm"
        closeOnEscape
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setExcluir(null)} disabled={saving}>
              Cancelar
            </Button>
            <Button variant="danger" onClick={() => void confirmarExclusao()} loading={saving} disabled={saving}>
              Excluir
            </Button>
          </div>
        }
      >
        <p className="text-sm text-slate-600">
          Excluir o período {excluir?.numero}
          {excluir ? ` (${formatDate(excluir.dataInicio)} – ${formatDate(excluir.dataFim)})` : ""}? Relatórios, medições
          e o avanço das casas permanecem como estão.
        </p>
      </Modal>
    </div>
  );
}

function Card({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-card">
      <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">{label}</p>
      <p className="mt-1 text-2xl font-bold text-slate-900 tabular-nums">{value}</p>
    </div>
  );
}

function ChartCard({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-card">
      <p className="text-sm font-semibold text-slate-700 mb-2">{title}</p>
      <div className="h-64">{children}</div>
    </div>
  );
}
